<template>
    <div class="esg-progress-block">
        <div v-if="!preview">
        <div class="d-flex justify-space-between align-center mb-2">
            <span class="block-title">{{ $t('esgPlatform.esgReport.esgProgressLineGraph') }}</span>
            <div class="d-flex align-center">
                <v-btn icon color="primary" class="mr-2" @click="showSettings = true">
                    <v-icon>mdi-cog</v-icon>
                </v-btn>
                <v-btn icon color="error" @click="$emit('delete')">
                    <v-icon>mdi-trash-can</v-icon>
                </v-btn>
            </div>
        </div>
        <div class="block-content">
            <div class="mb-2 block-label">{{ $t('esgPlatform.esgReport.selectEsgComponents') }}</div>
            <v-checkbox v-for="item in components" :key="item.value" v-model="block.selected" :label="item.label"
                :value="item.value" color="primary" hide-details class="mb-0" />
        </div>
        </div>

        <div v-else>
            <span class="block-title">{{ $t('esgPlatform.esgReport.esgProgressLineGraph') }}</span>
            <div class="d-flex justify-end mt-4 mb-2">
                <div v-if="block.selected.includes('environmental')" class="d-flex align-center mr-4">
                    <div class="rounded-circle mr-2" style="width: 10px; height: 10px; background-color: #4CAF50;">
                    </div>
                    {{ $t('esgPlatform.environmental') }}
                </div>
                <div v-if="block.selected.includes('social')" class="d-flex align-center mr-4">
                    <div class="rounded-circle mr-2" style="width: 10px; height: 10px; background-color: #2196F3;">
                    </div>
                    {{ $t('esgPlatform.social') }}
                </div>
                <div v-if="block.selected.includes('governance')" class="d-flex align-center">
                    <div class="rounded-circle mr-2" style="width: 10px; height: 10px; background-color: #FFC107;">
                    </div>
                    {{ $t('esgPlatform.governance') }}
                </div>
            </div>
            <apexchart type="line" :options="chartOptions" :series="filteredSeries" width="100%" height="300">
            </apexchart>
        </div>
        <ComponentSettingsDialog v-model="showSettings" :settings="settings" @save="handleSaveSettings" />
    </div>
</template>

<script>
import VueApexCharts from "vue-apexcharts";
import ComponentSettingsDialog from '../ComponentSettingDialog.vue';
import EsgService from '@/_services/EsgService';

export default {
    name: 'EsgProcessLineChartBlock',
    components: {
        ComponentSettingsDialog,
        apexchart: VueApexCharts,
    },
    props: {
        block: {
            type: Object,
            required: true
        },
        preview: {
            type: Boolean,
            default: false
        },
        protocolId: {
            type: String,
            default: null
        },
        targetYears: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            components: [
                { label: this.$t('esgPlatform.environmental'), value: 'environmental' },
                { label: this.$t('esgPlatform.social'), value: 'social' },
                { label: this.$t('esgPlatform.governance'), value: 'governance' }
            ],
            showSettings: false,
            settings: {
                bgColor: '#ffffff',
                fontColor: '#101010',
                font: 'Poppins'
            },
            series: [],
            chartOptions: {
                chart: {
                    type: 'line',
                    height: 400,
                    toolbar: { show: false },
                    zoom: { enabled: false }
                },
                stroke: { curve: 'smooth', width: 3 },
                markers: {
                    size: 5,
                    colors: ['#4CAF50', '#2196F3', '#FFC107'],
                    strokeColors: '#fff',
                    strokeWidth: 2,
                    hover: { size: 7 }
                },
                xaxis: {
                    categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
                    labels: { style: { colors: '#666', fontSize: '12px' } }
                },
                yaxis: {
                    min: 0,
                    max: 100,
                    tickAmount: 5,
                    labels: {
                        formatter: value => value + '%',
                        style: { colors: '#666', fontSize: '12px' }
                    }
                },
                colors: ['#4CAF50', '#2196F3', '#FFC107'],
                grid: { borderColor: '#f1f1f1' },
                legend: { show: false },
                tooltip: {
                    enabled: true,
                    y: { formatter: val => val + '%' }
                },
                dataLabels: { enabled: false }
            }
        }
    },
    computed: {
        filteredSeries() {
            if (!this.block.selected || this.block.selected.length === 0) {
                return [];
            }

            const componentMap = {
                environmental: 'Environmental',
                social: 'Social',
                governance: 'Governance',
            };

            // Filter series based on selected components
            return this.series.filter(seriesItem => {
                const selectedNames = (this.block.selected || []).map(
                    key => componentMap[key]
                );
                return selectedNames.includes(seriesItem.name);
            });
        },
        actualProtocolId() {
            // Get protocolId from prop, block data, or route
            return this.protocolId || this.block?.protocolId || this.$route?.params?.id;
        },
        actualTargetYears() {
            // Get targetYears from prop, block data, or convert from timeline
            if (this.targetYears && this.targetYears.length >= 1) {
                return [this.targetYears[0], this.targetYears[this.targetYears.length - 1]];
            }
            if (this.block?.targetYears && this.block.targetYears.length >= 2) {
                return [this.block.targetYears[0], this.block.targetYears[this.block.targetYears.length - 1]];
            }
            return [];
        }
    },
    watch: {
        preview: {
            immediate: true,
            handler(newVal) {
                if (newVal && this.actualProtocolId && this.actualTargetYears && this.actualTargetYears.length === 2) {
                    this.getEsgProgressChart();
                }
            }
        },
        actualTargetYears: {
            deep: true,
            handler() {
                if (this.preview && this.actualProtocolId && this.actualTargetYears && this.actualTargetYears.length === 2) {
                    this.getEsgProgressChart();
                }
            }
        }
    },
    created() {
        if (!this.block.selected) {
            this.$set(this.block, 'selected', ['environmental', 'social', 'governance']);
        }
        // Initialize empty series
        this.series = [
            {
                name: 'Environmental',
                data: Array(12).fill(0),
            },
            {
                name: 'Social',
                data: Array(12).fill(0),
            },
            {
                name: 'Governance',
                data: Array(12).fill(0),
            },
        ];
    },
    methods: {
        async getEsgProgressChart() {
            if (!this.actualProtocolId || !this.actualTargetYears || this.actualTargetYears.length !== 2) {
                return;
            }

            try {
                const queryParams = {
                    startTargetYear: this.actualTargetYears[0],
                    endTargetYear: this.actualTargetYears[1],
                };
                const response = await EsgService.getEsgProgressChart(this.actualProtocolId, queryParams);
                if (response.data && response.data.success) {
                    const { progress } = response.data.data;
                    
                    // Update series with actual data from API
                    this.series = [
                        {
                            name: 'Environmental',
                            data: Object.values(progress.Environmental || {}),
                        },
                        {
                            name: 'Social',
                            data: Object.values(progress.Social || {}),
                        },
                        {
                            name: 'Governance',
                            data: Object.values(progress.Governance || {}),
                        },
                    ];

                    // Update x-axis categories from the data
                    if (progress.Environmental && Object.keys(progress.Environmental).length > 0) {
                        this.chartOptions = {
                            ...this.chartOptions,
                            xaxis: {
                                ...this.chartOptions.xaxis,
                                categories: Object.keys(progress.Environmental),
                            }
                        };
                    }
                }
            } catch (error) {
                console.error('Error fetching ESG progress chart:', error);
            }
        },
        handleSaveSettings(settings) {
            this.block.settings = settings;
            this.showSettings = false;
        }
    }
}
</script>

<style scoped>
.esg-progress-block {
    background: #fafbfc;
    border-radius: 8px;
    padding: 24px;
    margin-bottom: 16px;
    position: relative;
}

.block-title {
    font-size: 20px;
    font-weight: 700;
    color: #222;
}

.block-label {
    font-size: 15px;
    font-weight: 500;
    color: #222;
    margin-bottom: 8px;
}
</style>